import React, { useMemo, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppsContainer from '@tenant/core/AppsContainer';
import { ROUTE_STRING } from 'constants/RouteString';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import { TabContext, TabPanel } from '@mui/lab';
import useGetDetailContainerApp from 'redux/slices/containerApp/hooks/useGetDetailContainerApp';
import StartAndStopProgress from './Common/StartAndStopProgress';
import ContainerAppDescription from './Detail/Tab/ContainerAppDescription';
import EnvironmentVariablesDescription from './Detail/EnvironmentVariablesDescription';
import ResourceAllocation from './Detail/Tab/ResourceAllocation';
import Networking from './Detail/Tab/Networking';
import Logs from './Detail/Tab/Logs';
import clsx from 'clsx';
import EditLayout from './Edit/EditLayout';
import AppLoader from '@tenant/core/AppLoader';
import Error404 from 'pages/errorPages/Error404';
import Executions from './Detail/Tab/Executions';
import DockerAppTypes from 'constants/DockerAppTypes';
import AppAccordion from '@tenant/core/App/Accordion/AppAccordion';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteContainerApp from './Common/DeleteContainerApp';
import { CONTAINER_APP_STATUS_ENUM } from '@tenant/utility/constants/enum';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';

const Detail = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState('Detail');
  const [isEdit, setIsEdit] = useState(false);
  const { appInstanceId, id: containerAppId } = useParams();
  const { data, isLoading, isFetching, isError, refetch } =
    useGetDetailContainerApp({
      appInstanceId,
      containerAppId,
    });
  const isShowExecutions = useMemo(
    () =>
      !!data?.data?.DeploymentType &&
      DockerAppTypes.find(
        (e) => e.Value.toString() === data.data.DeploymentType.toString(),
      )?.IsCron,
    [data?.data?.DeploymentType],
  );

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const callbackAfterDeleteContainer = () => {
    navigate(ROUTE_STRING.APP_INSTANCE.VIEW.replace(':id', appInstanceId));
  };

  return isError ? (
    <Error404 />
  ) : (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Library</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.APP_INSTANCE.LIST}>App Instances</Link>
          </Typography>
          <Typography color='text.primary'>
            <Link
              to={ROUTE_STRING.APP_INSTANCE.VIEW.replace(':id', appInstanceId)}
            >
              Detail
            </Link>
          </Typography>
          <Typography color='text.primary'>Container App</Typography>
        </Breadcrumbs>
      }
      sxStyle={{
        '& .apps-header': {
          height: 50,
        },
      }}
    >
      {(isLoading || isFetching) && <AppLoader />}

      <TabContext value={value}>
        <Box
          sx={{
            mb: '50px',
            '& .MuiDataGrid-root': {
              border: 0,
            },
          }}
        >
          <AppsHeader>
            <Grid container spacing={3}>
              <Grid item xs={20} sm={10}>
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  sx={{ width: '100%' }}
                >
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label='basic tabs example'
                  >
                    <Tab label='Detail' value='Detail' />
                    <Tab
                      label='Environment Variables'
                      value='Environment Variables'
                    />
                    <Tab
                      label='Resource Allocation'
                      value='Resource Allocation'
                    />
                    <Tab
                      label='Networking'
                      value='Networking'
                    />
                    {isShowExecutions && (
                      <Tab label='Job Executions' value='Job Executions' />
                    )}
                    <Tab label='Logs' value='Logs' />
                  </Tabs>
                </Box>
              </Grid>

              <Grid
                item
                xs={4}
                sm={2}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'end'}
                sx={{
                  '& .MuiButtonBase-root': {
                    ml: 2,
                    mr: 2,
                  },
                }}
              >
                {!!data?.data?.Status && (
                  <AllowedAccess
                    permissionIds={[
                      AuthorizePermission.APP_INSTANCE_CONTAINER_START,
                      AuthorizePermission.APP_INSTANCE_CONTAINER_STOP,
                    ]}
                  >
                    <StartAndStopProgress
                      status={data?.data?.Status}
                      appInstanceId={appInstanceId}
                      containerAppId={containerAppId}
                      callBack={refetch}
                    />
                  </AllowedAccess>
                )}

                {!!containerAppId &&
                  !!appInstanceId &&
                  data?.data?.Status !== CONTAINER_APP_STATUS_ENUM.Waiting && (
                    <AllowedAccess
                      permissionIds={[AuthorizePermission.APP_INSTANCE_DELETE]}
                    >
                      <DeleteContainerApp
                        containerName={data?.data?.AppComponentName}
                        containerAppId={containerAppId}
                        appInstanceId={appInstanceId}
                        callbackAfterSuccess={callbackAfterDeleteContainer}
                      />
                    </AllowedAccess>
                  )}
              </Grid>
            </Grid>
          </AppsHeader>
          <Box>
            <TabPanel value='Detail'>
              <AppAccordion
                title={
                  <Box
                    component='span'
                    display='flex'
                    alignItems={'center'}
                    justifyContent='space-between'
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    Essentials
                    <Tooltip title='Refresh'>
                      <IconButton
                        size='small'
                        sx={{ ml: 2 }}
                        onClick={() => !isFetching && refetch()}
                      >
                        <RefreshIcon fontSize='inherit' />
                      </IconButton>
                    </Tooltip>{' '}
                  </Box>
                }
              >
                <ContainerAppDescription
                  detail={data?.data ?? {}}
                  appInstanceId={appInstanceId}
                  containerAppId={containerAppId}
                  refetchDetail={refetch}
                />
              </AppAccordion>
            </TabPanel>
            <TabPanel value='Environment Variables'>
              <EnvironmentVariablesDescription
                environmentVariables={data?.data?.EnviromentVariables}
                appInstanceId={appInstanceId}
                containerAppId={containerAppId}
                refetchDetail={refetch}
              />
            </TabPanel>
            <TabPanel value='Resource Allocation'>
              <ResourceAllocation
                data={{
                  Cpu: data?.data?.Cpu,
                  Memory: data?.data?.Memory,
                  MinReplicas: data?.data?.MinReplicas,
                  MaxReplicas: data?.data?.MaxReplicas,
                  ConcurrentHttpRequests: data?.data?.ConcurrentHttpRequests,
                  CooldownPeriod: data?.data?.CooldownPeriod,
                }}
                appInstanceId={appInstanceId}
                containerAppId={containerAppId}
                refetchDetail={refetch}
              />
            </TabPanel>
            <TabPanel value='Networking'>
              <Networking
                data={{
                  isAccessRestricted: data?.data?.IsNetworkRestrictionEnabled,
                  whitelistedIPs: data?.data?.WhitelistIPs,
                  showRecommendedIPs: data?.data?.ShowRecommendedIPs,
                }}
                appInstanceId={appInstanceId}
                containerAppId={containerAppId}
                refetchDetail={refetch}
              />
            </TabPanel>
            <TabPanel value='Job Executions'>
              {isShowExecutions && <Executions />}
            </TabPanel>
            <TabPanel value='Logs'>
              <Logs />
            </TabPanel>
          </Box>
        </Box>
      </TabContext>

      <Box
        sx={{
          transition: 'all 0.5s ease',
          transform: 'translateX(100%)',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          opacity: 0,
          visibility: 'hidden',
          backgroundColor: 'background.paper',
          '&.show': {
            transform: 'translateX(0)',
            opacity: 1,
            visibility: 'visible',
          },
        }}
        className={clsx({
          show: isEdit,
        })}
      >
        {isEdit && <EditLayout onClickBackButton={() => setIsEdit(false)} />}
      </Box>
    </AppsContainer>
  );
};

export default React.memo(Detail);

Detail.propTypes = {};
